<template>
  <div class="bg-white p-3">
    <div v-if="data.fieldDaily && data.fieldDaily.length > 0" data-name="fieldMetricsDetailsInteractionsAndTimingsSection">
      <div class="row">
        <div class="col mb-3">
          <div class="card card-body d-block" data-name="fieldMetricsDetailsInteractionsSection">
            <i class="material-symbols-outlined display-4 text-primary d-block mb-1">timeline</i>
            <h5 class="mb-1">Interaction trends</h5>
            How do visitors interact or use your <code class="inline">{{ $props.field.display_name }}</code> field
            <line-chart :chart-options="lineChartOptionsRates" :chart-data="charts.rates" :height="250" class="flex-fill overflow-hidden mt-2 mb-2" data-name="fieldMetricsDetailsInteractionsGraph" />
          </div>
        </div>
        <div class="col mb-3">
          <div class="card card-body d-block" data-name="fieldMetricsDetailsTimingsSection">
            <i class="material-symbols-outlined display-4 text-primary d-block mb-1">timer</i>
            <h5 class="mb-1">Timing trends</h5>
            Time your visitors spend filling out the <code class="inline">{{ $props.field.display_name }}</code> field
            <line-chart :chart-options="lineChartOptionsTimings" :chart-data="charts.timings" :height="250" class="flex-fill overflow-hidden mt-2 mb-2" data-name="fieldMetricsDetailsTimingsGraph" />
          </div>
        </div>
      </div>
      <ErrorDetails :query="query" :field="field" />
    </div>
    <div v-else-if="loading" class="card card-body small text-secondary text-center mb-3">
      Loading trend and error details...
    </div>
    <div class="card">
      <div class="card-body">
        <i class="material-symbols-outlined display-4 text-primary d-block mb-1">code_blocks</i>
        <h5 class="mb-1">Field information</h5>
        <table class="tableTechInfo">
          <tr>
            <td>HTML name</td>
            <td @click="$copyText(field.name || '-')">
              <code class="inline cursor-pointer" v-b-tooltip title="Click to copy">{{ field.name || '-' }}</code>
            </td>
          </tr>
          <tr>
            <td>HTML identifier</td>
            <td @click="$copyText(field.identifier || '-')">
              <code class="inline cursor-pointer" v-b-tooltip title="Click to copy">{{ field.identifier || '-' }}</code>
            </td>
          </tr>
          <tr>
            <td>HTML location</td>
            <td @click="$copyText(field.qs_identifier || '-')">
              <code class="inline cursor-pointer" v-b-tooltip title="Click to copy">{{ field.qs_identifier || '-' }}</code>
            </td>
          </tr>
          <tr>
            <td>Updated date</td>
            <td @click="$copyText(field.updated_at | datetime)">
              <code class="inline cursor-pointer" v-b-tooltip title="Click to copy">{{ field.updated_at | datetime }}</code>
            </td>
          </tr>
          <tr>
            <td>ID</td>
            <td @click="$copyText(field.id)">
              <code class="inline cursor-pointer" v-b-tooltip title="Click to copy">{{ field.id }}</code>
              <router-link v-if="canEdit" class="btn btn-iso-create mb-1" :to="{ name: 'forms-edit', params: {id: $props.field.form_hash }}" style="font-size: 0.60rem; vertical-align: text-top;">
                EDIT FORM
              </router-link>
            </td>
          </tr>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tableTechInfo td {
  padding: 0.375rem;
}
.tableTechInfo td:first-child {
  font-weight: bold;
}
</style>

<script>
import {Line as LineChart} from "vue-chartjs/legacy";
import { Chart, registerables } from 'chart.js'
import ErrorDetails from "@/components/insights/ErrorDetails.vue";

Chart.register(...registerables)
Chart.defaults.elements.line.tension = 0.15
Chart.defaults.elements.line.borderWidth = 2
Chart.defaults.elements.point.radius = 0
Chart.defaults.scale.grid.drawBorder = false
Chart.defaults.animation.duration = !window.location.href.includes("src=grafo") ? 1000 : 0;

import 'chartjs-adapter-luxon'
import {resetSharedTooltips} from "@/vendor/libs/chartjs-shared-tooltips"

export default {
  name: "FieldMetricsDetails",
  components: {LineChart, ErrorDetails},
  props: ['field', 'filtersApplied', 'query', 'toggleStarts', 'formSessions'],
  data() {
    return {
      data: {
        loading: false,
        fieldDaily: null,
        formDaily: null,
      },
    }
  },
  computed: {
    canEdit () {
      return this.$root.hasRole('admin.internal') ||
        this.$root.hasRole('admin.company') ||
        this.$root.hasRole('configuration.modify')
    },
    lineChartOptionsRates () {
      const o = this.lineChartOptionsDefaults()

      o.scales.y.title = {
        display: true,
        text: 'Rate %'
      }

      o.scales.y1.title = {
        display: true,
        text: 'Interaction rate %'
      }

      o.plugins.tooltip.callbacks.label = function(context) {
        var label = context.dataset.label || ''
        if (label.length) {
          label += ': '
        }
        if (context.parsed.y !== null) {
          label += context.parsed.y + '%'
        }
        return label
      }

      return o
    },
    lineChartOptionsTimings () {
      const o = this.lineChartOptionsDefaults()

      o.scales.y.title = {
        display: true,
        text: 'Seconds'
      }

      delete o.scales.y1

      o.plugins.tooltip.callbacks.label = function(context) {
        var label = context.dataset.label || ''
        if (label.length) {
          label += ': '
        }
        if (context.parsed.y !== null) {
          label += context.parsed.y + 's'
        }
        return label
      }

      return o
    },
    charts () {
      if (!this.data.fieldDaily) {
        return null
      }

      return {
        timings: {
          datasets: [
            {
              yAxisID: 'y',
              label: 'Interaction time',
              data: this.data.fieldDaily.map(item => ({ x: item.day, y: item.interaction_time })),
              borderColor: '#007dfd',
              backgroundColor: '#007dfd',
              type: 'LineWithSharedTooltip',
              pointHitRadius: 25,
              fill: false
            },
            {
              yAxisID: 'y',
              label: 'Hesitation time',
              data: this.data.fieldDaily.map(item => ({ x: item.day, y: item.hesitation_time })),
              borderColor: '#FFCC66',
              backgroundColor: '#FFCC66',
              type: 'LineWithSharedTooltip',
              pointHitRadius: 25,
              fill: false
            },
          ]
        },
        rates: {
          datasets: [
            {
              label: 'Interaction rate',
              yAxisID: 'y1',
              data: this.data.fieldDaily.map(item => ({ x: item.day, y: Number(item.interactions / (this.formMetric(item.day).sessions || 1) * 100).toFixed(2) })),
              borderColor: '#007dfd',
              backgroundColor: '#007dfd',
              type: 'LineWithSharedTooltip',
              pointHitRadius: 25,
              fill: false,
            },
            {
              label: 'Autofill rate',
              data: this.data.fieldDaily.map(item => ({ x: item.day, y: item.autofill_rate })),
              borderColor: '#48d8c0',
              backgroundColor: '#48d8c0',
              type: 'LineWithSharedTooltip',
              pointHitRadius: 25,
              fill: false,
            },
            {
              label: 'Correction rate',
              data: this.data.fieldDaily.map(item => ({ x: item.day, y: item.correction_rate })),
              borderColor: '#FFCC66',
              backgroundColor: '#FFCC66',
              type: 'LineWithSharedTooltip',
              pointHitRadius: 25,
              fill: false,
            },
            {
              label: 'Dropoff rate',
              data: this.data.fieldDaily.map(item => ({ x: item.day, y: item.dropoff_rate })),
              borderColor: '#2E3237',
              backgroundColor: '#2E3237',
              type: 'LineWithSharedTooltip',
              pointHitRadius: 25,
              fill: false,
            },
            {
              label: 'Error rate',
              data: this.data.fieldDaily.map(item => ({ x: item.day, y: item.error_rate })),
              borderColor: '#ea8383',
              backgroundColor: '#ea8383',
              type: 'LineWithSharedTooltip',
              pointHitRadius: 25,
              fill: false,
            },
            {
              label: 'Rage click rate',
              data: this.data.fieldDaily.map(item => ({ x: item.day, y: item.rage_click_rate })),
              borderColor: '#e83e8c',
              backgroundColor: '#e83e8c',
              type: 'LineWithSharedTooltip',
              pointHitRadius: 25,
              fill: false,
            },
            {
              label: 'Blank rate',
              data: this.data.fieldDaily.map(item => ({ x: item.day, y: item.blank_rate })),
              borderColor: '#6610f2',
              backgroundColor: '#6610f2',
              type: 'LineWithSharedTooltip',
              pointHitRadius: 25,
              fill: false,
            },
          ]
        }
      }
    },
  },
  methods: {
    lineChartOptionsDefaults () {
      const o = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            // stacked: true,
            grid: {
              display: false
            },
            type: 'time',
            offset: true,
            time: {
              unit: 'day',
              tooltipFormat: 'DD'
            },
            ticks: {
              padding: 8,
            },
          },
          y: {
            grid: {
              display: true
            },
            ticks: {
              suggestedMin: 0,
              color: '#A7A7A7',
              autoSkipPadding: 30
            },
          },
          y1: {
            position: 'right',
            id: 'right-axis',
            grid: {
              display: false
            },
            ticks: {
              suggestedMin: 0,
              color: '#A7A7A7',
              autoSkipPadding: 30
            },
          }
        },
        plugins: {
          legend: {
            position: 'bottom',
            align: 'start',
            labels: {
              boxWidth: 12
            }
          },
          tooltip: {
            mode: 'index',
            backgroundColor: 'rgb(46, 50, 55)',
            padding: 16,
            cornerRadius: 0,
            caretSize: 16,
            boxPadding: 6,
            bodySpacing: 4,
            titleMarginBottom: 12,
            titleFont: {
              size: 16
            },
            callbacks: {}
          },
          annotation: { annotations: [] } // https://github.com/apertureless/vue-chartjs/issues/846#issuecomment-1149575966
        }
      }

      const clone = structuredClone(o)
      clone.scales.x.ticks.color = (t) => {
        const day = new Date(t.tick.value).getDay()
        return (day === 0 || day === 6) ? '#D7D7D7' : '#A7A7A7'
      }

      return clone
    },
    formMetric (day) {
      if (!this.data.formDaily?.length) return
      if (typeof day === 'undefined' || !day) return

      const result = this.data.formDaily.filter(f => f.day === day)
      return result.length ? result[0] : {}
    }
  },
  beforeDestroy() {
    resetSharedTooltips()
  },
  watch: {
    query: {
      immediate: true,
      async handler() {
        this.loading = true

        let fieldMetricsDaily = null
        let formMetricsDaily = null;

        [
          fieldMetricsDaily,
          formMetricsDaily,
        ] = await Promise.all([
          this.$api.post('/metrics/fields', this.query, this.$root.client.client_hash),
          this.$api.post('/metrics/forms', this.query, this.$root.client.client_hash),
        ])

        this.loading = false

        this.data.formDaily = formMetricsDaily.data.metrics
        this.data.fieldDaily = fieldMetricsDaily.data.metrics
          .filter(metric => metric.element_name === this.field.name || metric.element_name === this.field.identifier)
          .map(metric => {
            const formStarts = this.formMetric(metric.day)?.starts ?? metric.interactions
            return {
              ...metric,
              blanks: (formStarts - metric.interactions),
              blank_rate: Math.round((formStarts - metric.interactions) / (formStarts || 1) * 10000) / 100
            }
          })
      }
    }
  }
}
</script>
